import { memo, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { GoogleMap } from '@react-google-maps/api'
import { importLibrary } from '@googlemaps/js-api-loader'
import { Crosshair, Loader2, MapPinOff, Phone, RefreshCw, UserRound, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { resolveGoogleMapsStatusMessage, useGoogleMapsLoader } from '@/hooks/use-google-maps-loader'
import { cn } from '@/lib/utils'

import { realtimeConfig } from '../config'
import { useIsDarkMode } from '../hooks/use-environment'
import { useJobGeocoder, type GeocodeState } from '../hooks/use-job-geocoder'
import { FIT_NEAREST_CANDIDATES, schedulingRules, type FitPadding } from '../schedulingRules'
import { useRealtimeStore } from '../store/realtime-store'
import type { Job, MapLayer, Worker } from '../types'
import { clusterByPixelDistance, type Cluster } from '../utils/cluster'
import { estimateEtaMinutes, hasValidCoords, haversineKm } from '../utils/distance'
import type { NearbyCandidate } from '../utils/nearby-workers'
import { getActiveWorkerIds } from '../utils/job-workers'
import { WORKER_STATUS_LABEL, type JobDisplayStatus } from '../utils/job-status'
import { buildSelectionFitPoints, fitMapToPoints, resolveSelectionFocus, SELECTION_MAX_ZOOM } from '../utils/map-fit'
import { formatRelativeTime } from '../utils/time'
import { ClusterMarker, JobMarker } from './JobMarker'
import { MapHtmlMarker } from './map/MapHtmlMarker'
import {
  AssignedWorkerPin,
  CandidatePin,
  MissingLocationBanner,
  resolveWorkerPinTone,
  RouteLine,
  SelectedJobPin,
  WorkerRoute,
} from './map/SelectionLayer'
import { JOB_STATUS_VISUAL, WORKER_STATUS_VISUAL, WorkerAvatar, WorkerStatusBadge } from './status-meta'
import { WorkerMarker } from './WorkerMarker'

const CLUSTER_BELOW_ZOOM = 13
const WORLD_CENTER = { lat: 20, lng: 0 }
const INITIAL_ZOOM = 2

const DARK_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1f2a24' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#9fb3a8' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1f2a24' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2e3d35' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f1c24' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
]

export interface WorkersMapProps {
  serviceInstanceId: string | null
  /** Workers shown on the map (after KPI filtering). */
  workers: Worker[]
  /** Every worker, used to resolve the selected job's workers. */
  allWorkers: Worker[]
  jobs: Job[]
  /** The selected job (shared selection), even if the current filter hides it. */
  selectedJob: Job | null
  /** Nearby workers for the selected job (blue pins; the nearest few are framed by the camera). */
  candidates: NearbyCandidate[]
  /** False while candidates load, so the camera frames the selection once they are known. */
  candidatesSettled: boolean
  /** Popup / nearby strip rendered over the map (they position themselves). */
  overlays?: ReactNode
  /** The nearby strip covers the bottom of the map: the missing-location banner sits above it. */
  hasBottomOverlay?: boolean
  selectedWorkerId: string | null
  jobNumbers: Map<string, number>
  displayById: Map<string, JobDisplayStatus>
  layers: Record<MapLayer, boolean>
  onToggleLayer: (layer: MapLayer) => void
  /** Toggle (select/clear) a job. */
  onSelectJob: (jobId: string) => void
  /** Select without toggling (used after "Locate"). */
  onFocusJob: (jobId: string) => void
  onSelectWorker: (workerId: string | null) => void
  onClearSelection: () => void
  onlineCount: number
  workersWithoutLocation: number
  /** fitBounds runs once per distinct key (branch + day), after data is ready. */
  fitKey: string
  /** Per-side fit padding (panel / bottom sheet aware). */
  fitPadding: FitPadding
  isLoading: boolean
  reducedMotion: boolean
  now: number
  className?: string
}

type MarkerLibraryState = 'idle' | 'loading' | 'ready' | 'failed'

/** Loads only the `marker` library, and only when a mapId enables AdvancedMarkerElement. */
function useMarkerLibrary(enabled: boolean): MarkerLibraryState {
  const [state, setState] = useState<MarkerLibraryState>(enabled ? 'loading' : 'idle')
  useEffect(() => {
    if (!enabled) {
      setState('idle')
      return undefined
    }
    let cancelled = false
    setState('loading')
    importLibrary('marker')
      .then(() => !cancelled && setState('ready'))
      .catch(() => !cancelled && setState('failed'))
    return () => {
      cancelled = true
    }
  }, [enabled])
  return state
}

/** Worker popup, only used when no job is selected (the selected job uses the job popup + tags). */
function WorkerInfoCard({ worker, currentJob, now, onClose }: { worker: Worker; currentJob: Job | null; now: number; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement | null>(null)

  // The marker container is attached to the map pane asynchronously, so focus on the next frames.
  useEffect(() => {
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => closeRef.current?.focus({ preventScroll: true }))
    })
    return () => cancelAnimationFrame(frame)
  }, [worker.id])

  return (
    <div className="pb-6">
      <div role="dialog" aria-label={`${worker.name} details`} onClick={(event) => event.stopPropagation()} className="w-64 rounded-xl border border-[#dfeae3] bg-white p-3 text-left shadow-xl">
        <div className="flex items-start gap-2">
          <WorkerAvatar name={worker.name} avatarUrl={worker.avatarUrl} status={worker.status} className="h-9 w-9" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-[#20352b]">{worker.name}</p>
            <WorkerStatusBadge status={worker.status} className="mt-0.5" />
          </div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close worker details" className="rounded-md p-1 text-[#718078] hover:bg-[#eef6f1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#169b62]">
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <dl className="mt-3 space-y-1.5 text-[12px]">
          <div className="flex justify-between gap-3"><dt className="text-[#718078]">Phone</dt><dd className="truncate font-medium text-[#20352b]">{worker.phone || '—'}</dd></div>
          <div className="flex justify-between gap-3"><dt className="text-[#718078]">Current job</dt><dd className="truncate font-medium text-[#20352b]">{currentJob ? `${currentJob.bookingNo} · ${currentJob.service}` : '—'}</dd></div>
          <div className="flex justify-between gap-3"><dt className="text-[#718078]">Last seen</dt><dd className="font-medium text-[#20352b]">{worker.lastSeenAt ? formatRelativeTime(worker.lastSeenAt, now) : 'Unknown'}</dd></div>
        </dl>
        <div className="mt-3 flex gap-2">
          {worker.phone ? (
            <Button asChild size="sm" className="h-8 flex-1 gap-1.5 bg-[#169b62] text-xs hover:bg-[#118753]">
              <a href={`tel:${worker.phone.replace(/\s+/g, '')}`}><Phone className="h-3.5 w-3.5" aria-hidden="true" />Call</a>
            </Button>
          ) : (
            <Button size="sm" disabled className="h-8 flex-1 gap-1.5 text-xs"><Phone className="h-3.5 w-3.5" aria-hidden="true" />No phone</Button>
          )}
          <Button asChild size="sm" variant="outline" className="h-8 flex-1 gap-1.5 text-xs">
            <Link to={`/workers/${worker.id}`}><UserRound className="h-3.5 w-3.5" aria-hidden="true" />View profile</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}

function MissingJobsPopover({ jobs, geocodeStates, onLocate }: { jobs: Job[]; geocodeStates: Record<string, GeocodeState>; onLocate: (job: Job) => void }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(event) => event.stopPropagation()}
          className="inline-flex items-center gap-1 rounded-md bg-white/95 px-2 py-0.5 text-[10px] font-medium text-[#355445] underline-offset-2 shadow-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#169b62]"
          aria-label={`${jobs.length} jobs without location — show list`}
        >
          <MapPinOff className="h-3 w-3" aria-hidden="true" />
          {jobs.length} job{jobs.length === 1 ? '' : 's'} without location
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <p className="border-b px-3 py-2 text-xs font-semibold text-[#20352b]">Jobs without a map location</p>
        <ul className="max-h-72 divide-y overflow-y-auto">
          {jobs.map((job) => {
            const state = geocodeStates[job.id] ?? null
            return (
              <li key={job.id} className="flex items-start gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12px] font-semibold text-[#20352b]">{job.bookingNo} · {job.service}</p>
                  <p className="truncate text-[11px] text-[#718078]">{job.address || 'No address on file'}</p>
                  {state === 'failed' ? <p className="text-[11px] text-red-600">Address not found</p> : null}
                </div>
                <Button type="button" size="sm" variant="outline" disabled={!job.address || state === 'pending'} onClick={() => onLocate(job)} className="h-7 shrink-0 gap-1 px-2 text-[11px]" aria-label={`Locate ${job.bookingNo}`}>
                  {state === 'pending' ? <Loader2 className="h-3 w-3 motion-safe:animate-spin" aria-hidden="true" /> : <Crosshair className="h-3 w-3" aria-hidden="true" />}
                  Locate
                </Button>
              </li>
            )
          })}
        </ul>
      </PopoverContent>
    </Popover>
  )
}

function MapLegend() {
  return (
    <div className="pointer-events-auto rounded-lg bg-white/95 p-2 text-[10px] text-[#355445] shadow-md">
      <p className="mb-1 font-semibold uppercase tracking-wide text-[#718078]">Legend</p>
      <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
        {(Object.keys(WORKER_STATUS_VISUAL) as Array<keyof typeof WORKER_STATUS_VISUAL>).map((status) => {
          const visual = WORKER_STATUS_VISUAL[status]
          const Icon = visual.icon
          return (
            <li key={status} className="flex items-center gap-1.5">
              <span className={cn('flex h-4 w-4 items-center justify-center rounded-full text-white', visual.solid)}><Icon className="h-2.5 w-2.5" aria-hidden="true" /></span>
              {WORKER_STATUS_LABEL[status]}
            </li>
          )
        })}
        <li className="flex items-center gap-1.5">
          <svg viewBox="0 0 32 40" className="h-4 w-3" aria-hidden="true"><path d="M16 0C7.2 0 0 7.1 0 15.9 0 27.8 16 40 16 40s16-12.2 16-24.1C32 7.1 24.8 0 16 0z" fill={JOB_STATUS_VISUAL.unassigned.hex} /></svg>
          Job (numbered)
        </li>
        <li className="flex items-center gap-1.5"><span className="w-4 border-t-2 border-dotted border-indigo-500" aria-hidden="true" />En-route</li>
      </ul>
    </div>
  )
}

const LAYER_LABEL: Record<MapLayer, string> = { workers: 'Workers', jobs: 'Jobs', routes: 'Routes' }

function MapChrome({
  layers, onToggleLayer, onlineCount, workersWithoutLocation, jobsWithoutLocation, geocodeStates, onLocate, showLegend,
}: {
  layers: Record<MapLayer, boolean>
  onToggleLayer: (layer: MapLayer) => void
  onlineCount: number
  workersWithoutLocation: number
  jobsWithoutLocation: Job[]
  geocodeStates: Record<string, GeocodeState>
  onLocate: (job: Job) => void
  showLegend: boolean
}) {
  return (
    <>
      <div role="group" aria-label="Map layers" className="absolute left-3 top-3 z-10 flex overflow-hidden rounded-lg border border-[#dfeae3] bg-white shadow-sm">
        {(Object.keys(LAYER_LABEL) as MapLayer[]).map((layer) => (
          <button
            key={layer}
            type="button"
            aria-pressed={layers[layer]}
            onClick={(event) => {
              event.stopPropagation()
              onToggleLayer(layer)
            }}
            className={cn(
              'h-8 px-3 text-[11px] font-semibold transition-colors focus-visible:relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#169b62]',
              layers[layer] ? 'bg-[#169b62] text-white' : 'text-[#355445] hover:bg-[#eef6f1]',
            )}
          >
            {LAYER_LABEL[layer]}
          </button>
        ))}
      </div>
      <div className="absolute right-3 top-3 z-10 flex flex-col items-end gap-1">
        <span className="rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-[#087a49] shadow-sm">
          <span className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
          {onlineCount} online
        </span>
        {workersWithoutLocation > 0 ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-white/95 px-2 py-0.5 text-[10px] text-[#718078] shadow-sm">
            <MapPinOff className="h-3 w-3" aria-hidden="true" />
            {workersWithoutLocation} worker{workersWithoutLocation === 1 ? '' : 's'} without location
          </span>
        ) : null}
        {jobsWithoutLocation.length > 0 ? <MissingJobsPopover jobs={jobsWithoutLocation} geocodeStates={geocodeStates} onLocate={onLocate} /> : null}
      </div>
      {showLegend ? (
        <div className="pointer-events-none absolute bottom-3 left-3 z-10 hidden sm:block">
          <MapLegend />
        </div>
      ) : null}
    </>
  )
}

function MapCanvas({ useAdvanced, ...props }: WorkersMapProps & { useAdvanced: boolean }) {
  const {
    workers, allWorkers, jobs, selectedJob, candidates, candidatesSettled, overlays, hasBottomOverlay = false,
    selectedWorkerId, jobNumbers, displayById, layers, onToggleLayer, onSelectJob, onFocusJob, onSelectWorker,
    onClearSelection, onlineCount, workersWithoutLocation, fitKey, fitPadding, isLoading, reducedMotion, now,
    className,
  } = props
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [zoom, setZoom] = useState(INITIAL_ZOOM)
  const isDark = useIsDarkMode()
  const { geocodeJob, stateByJobId } = useJobGeocoder()
  const hoveredWorkerId = useRealtimeStore((state) => state.hoveredWorkerId)
  const setHoveredWorker = useRealtimeStore((state) => state.setHoveredWorker)
  const focusedCandidate = useRealtimeStore((state) => state.focusedCandidate)
  const focusCandidate = useRealtimeStore((state) => state.focusCandidate)

  const locatedWorkers = useMemo(() => workers.filter(hasValidCoords), [workers])
  const locatedJobs = useMemo(() => jobs.filter(hasValidCoords), [jobs])
  const jobsMissingLocation = useMemo(() => jobs.filter((job) => !hasValidCoords(job)), [jobs])
  const jobsById = useMemo(() => new Map(jobs.map((job) => [job.id, job])), [jobs])
  const workersById = useMemo(() => new Map(allWorkers.map((worker) => [worker.id, worker])), [allWorkers])

  // Dev-only diagnostics: which jobs cannot be plotted.
  const missingIdsKey = jobsMissingLocation.map((job) => job.id).join(',')
  useEffect(() => {
    if (import.meta.env.DEV && missingIdsKey) {
      const ids = missingIdsKey.split(',')
      console.warn(`[RealtimeMap] ${ids.length} job(s) skipped — no valid coordinates:`, ids)
    }
  }, [missingIdsKey])

  const geocodeState = selectedJob ? stateByJobId[selectedJob.id] ?? null : null
  const focus = useMemo(
    () => (selectedJob ? resolveSelectionFocus(selectedJob, allWorkers, { now, geocodeState }) : null),
    [allWorkers, geocodeState, now, selectedJob],
  )
  const focusDisplay = focus ? displayById.get(focus.job.id) : undefined
  const jobIsLate = focusDisplay?.kind === 'late' || focusDisplay?.kind === 'overdue'

  // Expanding a job without coordinates but with an address: geocode it on demand (once).
  const geocodeInputsRef = useRef({ selectedJob, stateByJobId })
  geocodeInputsRef.current = { selectedJob, stateByJobId }
  const selectedJobId = selectedJob?.id ?? null
  useEffect(() => {
    const { selectedJob: job, stateByJobId: states } = geocodeInputsRef.current
    if (!job || hasValidCoords(job) || !job.address || states[job.id]) return
    void geocodeJob(job)
  }, [geocodeJob, selectedJobId])

  const options = useMemo<google.maps.MapOptions>(() => ({
    mapId: realtimeConfig.mapId || undefined,
    styles: !realtimeConfig.mapId && isDark ? DARK_STYLES : undefined,
    disableDefaultUI: true,
    zoomControl: true,
    clickableIcons: false,
    gestureHandling: 'cooperative',
    keyboardShortcuts: true,
  }), [isDark])

  const overviewPoints = useMemo(() => [...locatedWorkers, ...locatedJobs], [locatedJobs, locatedWorkers])

  // Fit to everything once per branch/day after data has loaded (per map instance: StrictMode can create two).
  const fittedRef = useRef<{ map: google.maps.Map; key: string } | null>(null)
  useEffect(() => {
    if (!map || isLoading) return
    if (fittedRef.current?.map === map && fittedRef.current.key === fitKey) return
    fittedRef.current = { map, key: fitKey }
    fitMapToPoints(map, overviewPoints, { padding: fitPadding, maxZoom: SELECTION_MAX_ZOOM })
  }, [fitKey, fitPadding, isLoading, map, overviewPoints])

  // Camera follows the selected job: frame [job + assigned workers + nearest candidates] once the
  // candidates are known (and again after geocoding or when the mobile sheet moves); restore the
  // overview on deselect. Live events (workers added/removed/moving) never move the camera.
  // Debounced so bursts of changes cause one camera move.
  const focusCameraKey = focus ? `${focus.job.id}|${focus.jobHasLocation}|${candidatesSettled}|${fitPadding.bottom}` : null
  const cameraInputsRef = useRef({ focus, overviewPoints, fitPadding, candidates })
  cameraInputsRef.current = { focus, overviewPoints, fitPadding, candidates }
  const previousFocusKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!map) return undefined
    const previous = previousFocusKeyRef.current
    previousFocusKeyRef.current = focusCameraKey
    if (focusCameraKey === previous) return undefined
    const timer = setTimeout(() => {
      const { focus: current, overviewPoints: all, fitPadding: padding, candidates: nearby } = cameraInputsRef.current
      if (focusCameraKey && current) {
        const points = buildSelectionFitPoints(current, nearby, FIT_NEAREST_CANDIDATES)
        fitMapToPoints(map, points, { padding, maxZoom: schedulingRules.selectionMaxZoom })
      } else if (previous) {
        fitMapToPoints(map, all, { padding, maxZoom: SELECTION_MAX_ZOOM })
      }
    }, schedulingRules.fitDebounceMs)
    return () => clearTimeout(timer)
  }, [focusCameraKey, map])

  const focusWorkerIds = useMemo(() => new Set(focus?.workers.map((entry) => entry.worker.id) ?? []), [focus])
  const candidateIds = useMemo(() => new Set(focus ? candidates.map((candidate) => candidate.worker.id) : []), [candidates, focus])
  const hasFocus = Boolean(focus)
  const showTags = zoom >= schedulingRules.mapTagMinZoom

  // While a job is selected nothing is clustered, so the selection is never hidden in a cluster.
  const shouldCluster = zoom < CLUSTER_BELOW_ZOOM && !hasFocus
  const workerClusters = useMemo(() => {
    const rest = locatedWorkers.filter((worker) => !focusWorkerIds.has(worker.id) && !candidateIds.has(worker.id))
    const inputs = rest.map((worker) => ({ id: worker.id, position: { lat: worker.lat, lng: worker.lng }, item: worker }))
    return shouldCluster ? clusterByPixelDistance(inputs, zoom) : inputs.map((input) => ({ id: input.id, position: input.position, items: [input] }))
  }, [candidateIds, focusWorkerIds, locatedWorkers, shouldCluster, zoom])

  const jobGroups = useMemo(() => {
    const inputs = locatedJobs
      .filter((job) => job.id !== focus?.job.id)
      .map((job) => ({ id: job.id, position: { lat: job.lat, lng: job.lng }, item: job }))
    return shouldCluster ? clusterByPixelDistance(inputs, zoom) : inputs.map((input) => ({ id: input.id, position: input.position, items: [input] }))
  }, [focus?.job.id, locatedJobs, shouldCluster, zoom])

  const enRouteLines = useMemo(() => {
    if (hasFocus) return []
    return locatedJobs.flatMap((job) => {
      const kind = displayById.get(job.id)?.kind
      if (kind !== 'en_route' && !(kind === 'late' && job.status !== 'in_progress')) return []
      return getActiveWorkerIds(job).flatMap((workerId) => {
        const worker = workersById.get(workerId)
        if (!hasValidCoords(worker) || worker.currentJobId !== job.id) return []
        return [{ job, worker, eta: job.etaMinutes ?? estimateEtaMinutes(haversineKm(worker, job)), late: kind === 'late' }]
      })
    })
  }, [displayById, hasFocus, locatedJobs, workersById])

  const zoomToCluster = useCallback((cluster: Cluster<unknown>) => {
    if (!map) return
    fitMapToPoints(map, cluster.items.map((entry) => entry.position), { padding: 64, maxZoom: SELECTION_MAX_ZOOM + 1 })
  }, [map])

  const handleLocate = useCallback(async (job: Job) => {
    const coords = await geocodeJob(job, { force: true })
    if (coords) onFocusJob(job.id)
  }, [geocodeJob, onFocusJob])

  /** Clicking an assigned worker's pin highlights (and reveals) their row in the card. */
  const focusJobId = focus?.job.id ?? null
  const activateWorkerRow = useCallback((workerId: string) => {
    setHoveredWorker(workerId)
    document.querySelectorAll<HTMLElement>('[data-worker-row]').forEach((row) => {
      if (row.dataset.workerRow === `${focusJobId}|${workerId}`) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
  }, [focusJobId, setHoveredWorker])

  const selectedWorker = !hasFocus && selectedWorkerId ? workersById.get(selectedWorkerId) ?? null : null
  const animate = !reducedMotion

  return (
    <div
      className={cn('relative overflow-hidden rounded-xl border border-[#dfeae3] bg-[#eef3ef]', className)}
      aria-label="Live map of workers and jobs"
      role="region"
    >
      <GoogleMap
        mapContainerClassName="h-full w-full"
        center={WORLD_CENTER}
        zoom={INITIAL_ZOOM}
        options={options}
        onLoad={(instance) => {
          // Listen on the instance directly so the handler never sees a stale `map`.
          instance.addListener('idle', () => setZoom(instance.getZoom() ?? INITIAL_ZOOM))
          setMap(instance)
        }}
        onUnmount={() => setMap(null)}
        onClick={onClearSelection}
      />

      {map ? (
        <>
          {layers.routes
            ? enRouteLines.map(({ job, worker, late }) => (
                <RouteLine key={`route-${job.id}-${worker.id}`} map={map} path={[worker, job]} color={late ? '#dc2626' : '#6366f1'} />
              ))
            : null}
          {layers.routes
            ? enRouteLines.map(({ job, worker, eta, late }) => (
                <MapHtmlMarker key={`eta-${job.id}-${worker.id}`} map={map} lat={(worker.lat + job.lat) / 2} lng={(worker.lng + job.lng) / 2} zIndex={5} animate={animate} useAdvanced={useAdvanced}>
                  <span className={cn('pointer-events-none block whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold text-white shadow', late ? 'bg-red-600' : 'bg-indigo-600')}>ETA {eta} min</span>
                </MapHtmlMarker>
              ))
            : null}

          {layers.jobs
            ? jobGroups.map((group) => {
                if (group.items.length > 1) {
                  return (
                    <MapHtmlMarker key={group.id} map={map} lat={group.position.lat} lng={group.position.lng} zIndex={10} useAdvanced={useAdvanced}>
                      <ClusterMarker count={group.items.length} kind="jobs" label={group.items.map((entry) => entry.item.bookingNo).join(', ')} onZoom={() => zoomToCluster(group)} />
                    </MapHtmlMarker>
                  )
                }
                const job = group.items[0].item
                const display = displayById.get(job.id)
                if (!display) return null
                return (
                  <MapHtmlMarker key={job.id} map={map} lat={group.position.lat} lng={group.position.lng} zIndex={10} useAdvanced={useAdvanced}>
                    <JobMarker job={job} number={jobNumbers.get(job.id) ?? 0} display={display} isSelected={false} dimmed={hasFocus} onSelect={onSelectJob} />
                  </MapHtmlMarker>
                )
              })
            : null}

          {layers.workers
            ? workerClusters.map((group) => {
                if (group.items.length > 1) {
                  return (
                    <MapHtmlMarker key={group.id} map={map} lat={group.position.lat} lng={group.position.lng} zIndex={20} useAdvanced={useAdvanced}>
                      <ClusterMarker count={group.items.length} kind="workers" label={group.items.map((entry) => entry.item.name).join(', ')} onZoom={() => zoomToCluster(group)} />
                    </MapHtmlMarker>
                  )
                }
                const worker = group.items[0].item
                if (!hasValidCoords(worker)) return null
                return (
                  <MapHtmlMarker key={worker.id} map={map} lat={worker.lat} lng={worker.lng} zIndex={20} animate={animate} useAdvanced={useAdvanced}>
                    <WorkerMarker worker={worker} isSelected={worker.id === selectedWorkerId} nearest={null} dimmed={hasFocus} onSelect={onSelectWorker} />
                  </MapHtmlMarker>
                )
              })
            : null}

          {/* Selection layer: selected job, its workers, routes and nearby candidates — never clustered or dimmed. */}
          {focus ? (
            <>
              {focus.workers.map((entry) => (entry.route ? (
                <WorkerRoute key={`focus-route-${entry.worker.id}`} map={map} entry={entry} tone={resolveWorkerPinTone(entry, jobIsLate)} useAdvanced={useAdvanced} animate={animate} />
              ) : null))}
              {candidates.map(({ worker, distanceKm }) => (hasValidCoords(worker) ? (
                <MapHtmlMarker key={`candidate-${worker.id}`} map={map} lat={worker.lat} lng={worker.lng} zIndex={hoveredWorkerId === worker.id ? 88 : 65} animate={animate} useAdvanced={useAdvanced}>
                  <CandidatePin
                    worker={worker}
                    distanceKm={distanceKm}
                    showTag={showTags}
                    isHovered={hoveredWorkerId === worker.id}
                    isFocused={focusedCandidate?.workerId === worker.id}
                    animate={animate}
                    onHover={setHoveredWorker}
                    onActivate={focusCandidate}
                  />
                </MapHtmlMarker>
              ) : null))}
              {focus.workers.map((entry) => (entry.located ? (
                <MapHtmlMarker key={`focus-worker-${entry.worker.id}`} map={map} lat={entry.worker.lat as number} lng={entry.worker.lng as number} zIndex={hoveredWorkerId === entry.worker.id ? 90 : 70} animate={animate} useAdvanced={useAdvanced}>
                  <AssignedWorkerPin
                    entry={entry}
                    tone={resolveWorkerPinTone(entry, jobIsLate)}
                    distanceKm={entry.route?.distanceKm ?? null}
                    showTag={showTags}
                    isHovered={hoveredWorkerId === entry.worker.id}
                    animate={animate}
                    now={now}
                    onHover={setHoveredWorker}
                    onActivate={activateWorkerRow}
                  />
                </MapHtmlMarker>
              ) : null))}
              {focus.jobHasLocation && focusDisplay ? (
                <MapHtmlMarker key={`focus-job-${focus.job.id}`} map={map} lat={focus.job.lat as number} lng={focus.job.lng as number} zIndex={80} useAdvanced={useAdvanced}>
                  <SelectedJobPin job={focus.job} number={jobNumbers.get(focus.job.id) ?? 0} display={focusDisplay} showTag={showTags} animate={animate} />
                </MapHtmlMarker>
              ) : null}
            </>
          ) : null}

          {selectedWorker && hasValidCoords(selectedWorker) ? (
            <MapHtmlMarker key={`info-${selectedWorker.id}`} map={map} lat={selectedWorker.lat} lng={selectedWorker.lng} zIndex={1000} animate={animate} useAdvanced={useAdvanced}>
              <WorkerInfoCard
                worker={selectedWorker}
                currentJob={selectedWorker.currentJobId ? jobsById.get(selectedWorker.currentJobId) ?? null : null}
                now={now}
                onClose={() => onSelectWorker(null)}
              />
            </MapHtmlMarker>
          ) : null}
        </>
      ) : null}

      <MapChrome
        layers={layers}
        onToggleLayer={onToggleLayer}
        onlineCount={onlineCount}
        workersWithoutLocation={workersWithoutLocation}
        jobsWithoutLocation={jobsMissingLocation}
        geocodeStates={stateByJobId}
        onLocate={(job) => void handleLocate(job)}
        showLegend={!focus}
      />

      {overlays}

      {focus ? (
        <MissingLocationBanner
          focus={focus}
          geocodeState={geocodeState}
          onLocate={() => void handleLocate(focus.job)}
          className={hasBottomOverlay ? 'bottom-[100px]' : 'top-14'}
        />
      ) : null}

      {isLoading ? <Skeleton className="absolute inset-0 rounded-none" aria-label="Loading map data" /> : null}
    </div>
  )
}

function MapUnavailable({ title, message, onRetry, className }: { title: string; message: string; onRetry?: () => void; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#cfe0d6] bg-[#f7fbf8] p-6 text-center', className)} role="alert">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#718078] shadow-sm"><MapPinOff className="h-5 w-5" aria-hidden="true" /></span>
      <p className="text-sm font-semibold text-[#20352b]">{title}</p>
      <p className="max-w-sm text-xs text-[#718078]">{message}</p>
      {onRetry ? (
        <Button type="button" size="sm" variant="outline" onClick={onRetry} className="mt-1 gap-1.5"><RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />Retry</Button>
      ) : null}
    </div>
  )
}

function WorkersMapLoader(props: WorkersMapProps & { onRetryLoad: () => void }) {
  const { onRetryLoad, ...mapProps } = props
  const { status, loadError } = useGoogleMapsLoader({ serviceInstanceReference: props.serviceInstanceId })
  const markerLibrary = useMarkerLibrary(status === 'ready' && Boolean(realtimeConfig.mapId))

  if (status === 'missing-key') {
    return <MapUnavailable className={props.className} title="Map not configured" message="Add a Google Maps key under organisation credentials or set VITE_GOOGLE_MAPS_API_KEY. The jobs list and schedule keep working." />
  }
  if (status === 'error' || status === 'disabled') {
    return <MapUnavailable className={props.className} title="Map failed to load" message={resolveGoogleMapsStatusMessage(status, loadError)} onRetry={status === 'error' ? onRetryLoad : undefined} />
  }
  if (status !== 'ready' || markerLibrary === 'loading') {
    return <Skeleton className={cn('rounded-xl', props.className)} aria-label="Loading map" />
  }
  return <MapCanvas {...mapProps} useAdvanced={markerLibrary === 'ready'} />
}

export const WorkersMap = memo(function WorkersMap(props: WorkersMapProps) {
  const [attempt, setAttempt] = useState(0)
  return <WorkersMapLoader key={attempt} {...props} onRetryLoad={() => setAttempt((value) => value + 1)} />
})
